import Head from 'next/head'
import { GTM_CLICK_EVENT, getEventUserType, sendGTMCustomClickEvent } from '../../../../helpers/gtmHelper'
import { useCurrentUser } from '../../../../modules/context/UserContext'
import useAppRouter from '../../../../modules/hooks/common/useAppRouter'
import { n_landing } from '../../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../../modules/i18n/useTranslation'
import { STUDENT_BTN_ID } from '../../../../modules/ids/gtm/student'
import links from '../../../../modules/links'
import urls from '../../../../modules/urls'
import NavLogo from '../../navigation/LandingNav/MainNav/NavLogo'
import AppDownloadButtons from './AppDownloadButtons'
import FooterNav from './FooterNav'
import InquiryButtons from './InquiryButtons'
import RingleInfo from './RingleInfo'
import SocialButtons from './SocialButtons'

const Footer = () => {
  const { t } = useTranslation()
  const router = useAppRouter()
  const { currentUser } = useCurrentUser()

  const handleMenuClick = (btnName: string, href: string | null, newTab = false) => {
    sendGTMCustomClickEvent(
      GTM_CLICK_EVENT.CLICK_LANDING_BUTTON,
      STUDENT_BTN_ID.CLICK_FOOTER_BUTTON,
      btnName,
      getEventUserType(currentUser),
      `${router.asPath.substring(1).split('/').join('-')}}`,
      router.asPath
    )

    newTab ? window.open(href, '_blank', 'noopener,noreferrer') : href && router.push(href)
  }

  return (
    <>
      <Head>
        {/* eslint-disable-next-line @next/next/no-page-custom-font */}
        <link href="https://fonts.googleapis.com/css2?family=Adamina&display=swap" rel="stylesheet" />
      </Head>
      <footer className="max-w-[1180px] sm:w-[1180px] mx-auto">
        <div className="px-20 sm:px-32 py-84 sm:pb-[124px] flex flex-col gap-y-44">
          <section className="flex sm:flex-row flex-col gap-x-20 gap-y-44 sm:gap-y-0">
            <div className="sm:min-w-[27%] flex flex-col gap-y-20 sm:gap-y-44">
              <NavLogo />
              <div className="sm:hidden block">
                <SocialButtons buttonClassName="h-40 w-40" onButtonClick={handleMenuClick} />
              </div>
              <div className="hidden sm:flex sm:flex-col sm:gap-y-44">
                <RingleInfo handleMenuClick={handleMenuClick} />
              </div>
            </div>
            <div className="sm:w-full flex gap-x-24">
              <div className="w-1/2 sm:w-3/5 flex flex-col sm:flex-row gap-y-24 sm:gap-x-24">
                <div className="sm:w-1/2">
                  <FooterNav title={t(n_landing.footer.title_1)} items={ringleProducts} />
                </div>
                <div className="sm:w-1/2">
                  <FooterNav title={t(n_landing.footer.title_2)} items={ringleCustomerCases} />
                </div>
              </div>
              <div className="sm:w-1/5 w-1/2 flex flex-col sm:gap-y-44 gap-y-24">
                <FooterNav title={t(n_landing.footer.title_3)} items={ringleBlog} />
                <FooterNav title={t(n_landing.footer.title_4)} items={ringleCompany} />
              </div>
            </div>
          </section>
          <InquiryButtons onClick={handleMenuClick} />
          <div className="flex sm:flex-col flex-col-reverse gap-y-44">
            <section>
              <span className="Body2-14Md text-gray-500 whitespace-pre-wrap">{t(n_landing.footer.desc_4)}</span>
            </section>
            <section className="flex justify-between items-center">
              <AppDownloadButtons onButtonClick={handleMenuClick} />
              <div className="hidden sm:block">
                <SocialButtons buttonClassName="h-40 w-40" onButtonClick={handleMenuClick} />
              </div>
            </section>
          </div>
          <div className="sm:hidden flex flex-col gap-y-44">
            <RingleInfo handleMenuClick={handleMenuClick} />
          </div>
        </div>
      </footer>
    </>
  )
}

export default Footer

const ringleProducts = [
  {
    name: n_landing.footer_title_1.list_1,
    link: urls.student.adv.index(links.framer.advPath['1on1'].index),
  },
  {
    name: n_landing.footer_title_1.list_2,
    link: urls.student.adv.index(links.framer.advPath.aiTutor.index),
  },
  {
    name: n_landing.footer_title_1.list_3,
    link: links.speakingTest.index,
  },
  {
    name: n_landing.footer_title_1.list_5,
    link: urls.b2b.index,
  },
  {
    name: n_landing.footer_title_1.list_6,
    link: links.teens.index,
  },
]

const ringleCustomerCases = [
  {
    name: n_landing.footer_title_2.list_1,
    link: urls.student.landing.blog.detail('?type=blog#1-1'),
  },
  { name: n_landing.footer_title_2.list_2, link: urls.student.landing.blog.detail('?type=blog#1-2') },
]

const ringleBlog = [
  {
    name: n_landing.footer_title_3.list_1,
    link: urls.student.landing.blog.detail('?type=blog#3'),
  },
  {
    name: n_landing.footer_title_3.list_2,
    link: urls.student.landing.blog.detail('?type=blog#4'),
  },
  {
    name: n_landing.footer_title_3.list_3,
    link: urls.student.landing.blog.index,
  },
]

const ringleCompany = [
  {
    name: n_landing.footer_title_4.list_1,
    link: 'https://www.ringleplus.com//ko/student/landing/team',
  },
  {
    name: n_landing.footer_title_4.list_2,
    link: links.career.index,
  },
]
